import currency from 'currency.js';

import { Currency, PageType } from '@/modules/models';

export const parseShopifyUrl = (url: string) => {
  const urlObj = new URL(url);
  const pathname = urlObj.pathname;
  const searchParams = new URLSearchParams(urlObj.search);

  // Process the pathname to get the part after /products/
  const productId = pathname.split('/products/')[1];

  const decodedProductId = productId ? decodeURIComponent(productId) : productId;

  // Extract the variant value
  const variantId = searchParams.get('variant');

  let pageType: PageType;
  if (pathname.includes('/about-us')) {
    // https://nununu.com/pages/about-us
    pageType = PageType.ABOUT;
  } else if (pathname.includes('/products')) {
    // — any product page
    pageType = PageType.PRODUCT;
  } else if (pathname === '/') {
    // https://nununu.com/
    pageType = PageType.HOME;
  } else {
    pageType = PageType.DEFAULT; //— any other page, like https://nununu.com/collections/girls
  }

  return { productId: decodedProductId, variantId, pageType };
};

export const setMobileInitialState = (val: boolean): void => {
  localStorage.setItem('isMobileOpen', String(val));
};

export const formatPrice = (price: number | string, symbol?: Currency) => {
  return currency(price, { symbol }).format();
};

export const wrapURL = (input: string) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const anchorTagRegex = /<a\s+href=["'](https?:\/\/[^\s]+)["'][^>]*>[^<]*<\/a>/g;

  return input.replace(urlRegex, (url) => {
    if (!anchorTagRegex.test(input)) {
      return `<a href="${url}" target="_blank">${url}</a>`;
    }
    return url;
  });
};

export const getQuantityFromSite = () => {
  const dom =
    document.querySelector<HTMLInputElement>('#quantity') ||
    document.querySelector<HTMLInputElement>('input[name="quantity"]');

  const value = Number(dom?.value);

  return isNaN(value) ? value : 1;
};

export const isIOS = () => {
  return (
    ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
  );
};

export const isDemoPage = window.location.href.indexOf('middleware') > -1 && window.location.href.indexOf('demo') > -1;

export const isWordpressDemo = window.location.href.indexOf('start-here.ahava.com') > -1;

export const getProductUrl = () =>
  document.querySelector('.yotpo.yotpo-main-widget[data-url]')?.getAttribute('data-url') || window.location.href; // data-url for live preview for AHAVA

export const getProductIdType = ({ product_id, pageType }: { product_id: string | null; pageType: PageType }) => {
  switch (true) {
    case isDemoPage:
      return {
        pageType: PageType.PRODUCT,
        product_id: 'abisko-dome-2',
      };

    case isWordpressDemo:
      return {
        pageType: PageType.AHAVA_WORDPRESS,
        product_id,
      };

    case pageType === PageType.CART:
      return {
        pageType,
        product_id: null,
      };

    default:
      return {
        pageType,
        product_id,
      };
  }
};

export const getProductSlugFromUrl = (url: string) => {
  if (url.includes('http')) {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;
    return pathname.split('/products/')?.[1] as string;
  } else {
    const match = url.match(/\/products\/([^?]+)/);
    return match?.[1] ?? '';
  }
};

export const isHebrew = (text: string) => /[\u0590-\u05FF]/.test(text);

export const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function getViewportContent(): string {
  const meta = document.querySelector('meta[name="viewport"]');
  return meta?.getAttribute('content') || '';
}

export const setViewportContent = (newContent: string) => {
  let meta = document.querySelector('meta[name="viewport"]');
  if (!meta) {
    meta = document.createElement('meta');
    const metaElement = meta as HTMLMetaElement;
    metaElement.name = 'viewport';
    document.head.appendChild(metaElement);
    meta = metaElement;
  }
  meta.setAttribute('content', newContent);
}

export const addUserScalableNo = () => {
  const current = getViewportContent();
  if (!current.includes('user-scalable')) {
    setViewportContent(`${current}, user-scalable=no`);
  }
}

export const removeUserScalable = () => {
  const current = getViewportContent();
  const updated = current
    .split(',')
    .map(v => v.trim())
    .filter(v => !v.startsWith('user-scalable'))
    .join(', ');
  setViewportContent(updated);
}